import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import { Col, Loading, Modal, Panel, Row } from '@elotech/components';
import { InlineButton } from 'common/components';
import { SILENT_REJECTION } from 'common/components';
import { useLoading } from 'common/hooks';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import { Document, Page } from 'react-pdf';
import { useSelector } from 'react-redux';
import {
  ArquivoAssinaturaService,
  ArquivoService,
  AssinaturaConfiguracaoService
} from 'service';
import { getIsLoggedSelector } from 'state';

import { Assinatura } from './Assinatura';
import { PdfPagination } from './PdfPagination';

const AssinaturaPosicionamentoContext = createContext(() =>
  Promise.resolve({
    x: 0,
    y: 0,
    page: 1,
    ratio: 1
  })
);

export const useAssinaturaPosicionamentoContext = () =>
  useContext(AssinaturaPosicionamentoContext);

export const AssinaturaPosicionamento = ({ children }) => {
  const [assinatura, setAssinatura] = useState();
  const [loading, setLoading] = useLoading();
  const [arquivo, setArquivo] = useState();
  const [showAssinatura, setShowAssinatura] = useState(false);
  const [rejecter, setRejecter] = useState();
  const [resolver, setResolver] = useState();
  const [configuracao, setConfiguracao] = useState();
  const isLogged = useSelector(getIsLoggedSelector);
  const [posicionou, setPosicionou] = useState(false);
  const [initialPosition, setInitialPosition] = useState();

  useEffect(() => {
    if (!isLogged) {
      return;
    }
    AssinaturaConfiguracaoService.findByEntidadeLogada().then(({ data }) => {
      const carimboData = data?.carimboArquivo?.content?.binary;
      const mimeType = data?.carimboArquivo?.content?.mimeType;

      data.carimboUrl =
        mimeType && carimboData && `data:${mimeType};base64,${carimboData}`;

      setConfiguracao(data);
    });
  }, [isLogged]);

  const rejectAll = useCallback(() => {
    setResolver(null);
    setAssinatura(null);
    setArquivo(null);
    if (rejecter) {
      rejecter(SILENT_REJECTION);
      setRejecter(null);
    }
  }, [setResolver, setAssinatura, setArquivo, rejecter, setRejecter]);

  const handleAssinatura = arquivo =>
    new Promise((resolve, reject) => {
      const init = () => {
        setAssinatura(arquivo);
        setInitialPosition(null);
        setPosicionou(false);
        setResolver(() => resolve);
        setRejecter(() => reject);
      };

      setLoading(
        ArquivoAssinaturaService.utilizaAssinaturaPosicional(
          arquivo.identificador
        ).then(({ data }) => {
          if (data) {
            return init();
          }
          return resolve(null);
        })
      );
    });

  //Limpa o BLOB da memória
  useEffect(() => {
    const oldArquivo = arquivo?.url;
    if (oldArquivo) {
      return () => {
        try {
          URL.revokeObjectURL(oldArquivo);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Falha ao liberar arquivo da memória', error);
        }
      };
    }
  }, [arquivo]);

  useEffect(() => {
    if (!assinatura?.identificador) {
      setArquivo(null);
      return;
    }
    let cancel = false;
    setLoading(
      ArquivoService.rawDownload(assinatura).then(({ data }) => {
        if (cancel) {
          return;
        }
        const blob = new Blob([data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setArquivo({ x: 0, y: 0, page: 1, ratio: 1, url });
      })
    );

    return () => {
      cancel = true;
    };
  }, [assinatura]);

  const handleLoadedPdf = pdf => {
    setArquivo({
      ...arquivo,
      ratio: 1,
      page: pdf.numPages,
      pages: pdf.numPages
    });
  };

  const handleLoadedPage = page => {
    setArquivo({
      ...arquivo,
      pageRatio: page.width / page.originalWidth
    });
    const newInitialPosition = {
      x: page.width * 0.7,
      y: page.height * 0.9
    };
    if (!initialPosition) {
      setInitialPosition(newInitialPosition);
      setPosicionou(false);
    }
    setShowAssinatura(true);
  };

  const sign = useCallback(() => {
    const { x, y, page, ratio, pageRatio } = arquivo;
    setAssinatura(null);
    resolver?.({
      x: x / pageRatio,
      y: y / pageRatio,
      page,
      ratio
    });
  }, [resolver, arquivo, setAssinatura]);

  return (
    <>
      <Loading loading={loading} />
      <AssinaturaPosicionamentoContext.Provider value={handleAssinatura}>
        {!!arquivo && (
          <Modal onClose={rejectAll}>
            <Panel title={`Assinando arquivo ${assinatura?.nome}`}>
              <Row>
                <Col md={4} xs={4}>
                  <Row>
                    <Col md={12} xs={12}>
                      <Row>
                        <PdfPagination
                          totalPages={arquivo.pages}
                          page={arquivo.page}
                          setPage={page => setArquivo({ ...arquivo, page })}
                        />
                      </Row>
                      <Row className="mt-xs ml-xs mr-xs">
                        Escala
                        <input
                          type="range"
                          min={0.5}
                          max={1}
                          step={0.01}
                          className="inline"
                          value={arquivo.ratio}
                          onChange={e =>
                            setArquivo({ ...arquivo, ratio: e.target.value })
                          }
                        />
                      </Row>
                      <Row>
                        <InlineButton
                          onClick={sign}
                          icon="fa fa-signature"
                          label="Assinar"
                          className="mt-xs ml-xs"
                          disabled={!posicionou}
                        />
                      </Row>
                    </Col>
                  </Row>
                </Col>
                <Col md={8} xs={8} className="center bg-gray-70">
                  <div
                    style={{
                      position: 'relative',
                      width: '500px',
                      margin: '10px auto'
                    }}
                  >
                    <Document
                      loading="Carregando PDF..."
                      noData="Nenhum PDF encontrado"
                      error="Erro ao carregar PDF"
                      className="center"
                      file={arquivo.url}
                      onLoadStart={() => setShowAssinatura(false)}
                      onLoadSuccess={handleLoadedPdf}
                      onLoadError={() => setShowAssinatura(false)}
                    >
                      <Page
                        renderAnnotationLayer
                        pageNumber={arquivo.page}
                        width={500}
                        onLoadStart={() => setShowAssinatura(false)}
                        onLoadError={() => setShowAssinatura(false)}
                        onLoadSuccess={handleLoadedPage}
                        error="Falha ao carregar a página"
                      >
                        <Assinatura
                          setArquivo={setArquivo}
                          hidden={!showAssinatura}
                          ratio={arquivo.ratio}
                          pageRatio={arquivo.pageRatio}
                          configuracao={configuracao}
                          setPosicionou={setPosicionou}
                          posicionou={posicionou}
                          initialPosition={initialPosition}
                        />
                      </Page>
                    </Document>
                  </div>
                </Col>
              </Row>
            </Panel>
          </Modal>
        )}
        {children}
      </AssinaturaPosicionamentoContext.Provider>
    </>
  );
};
